<template>
  <div>
    <NavBar :user="user"></NavBar>

    <section class="detail-layout tracker" id="tracker-details" v-if="tracker">
      <div class="detail-header tracker-header">
        <div class="container">
          <div class="row">
            <div class="col-12">
              <h3>
                <i class="material-icons">access_time</i>
                <span data-test="last-update" v-if="tracker.lastUpdate">
                  {{ tracker.lastUpdateRelative }}
                </span>
                <span v-else>-</span>
                |
                <span data-test="online-info">
                  <div class="gps gps_online" v-if="tracker.isOnline">Online</div>
                  <div class="gps gps_offline" v-else>Offline</div>
                </span>
              </h3>

              <h1 data-test="tracker-name">{{ tracker.name }}</h1>
              <h6>
                <i class="material-icons">fingerprint</i>
                {{ tracker.name }}
              </h6>

              <div class="inline-item">
                <i class="material-icons">battery_charging_full</i>
                <span data-test="battery-info-text">
                  <span
                    v-if="lastBattery"
                    :class="
                      lastBattery >= 75
                        ? 'battery_full'
                        : lastBattery >= 20
                        ? 'battery_okay'
                        : 'battery_critical'
                    "
                  >
                    {{ tracker.lastBatteryPercent | toInt }}%
                  </span>
                  <span v-else>-</span>
                </span>
              </div>

              <div class="inline-item">
                <i class="material-icons">av_timer</i>
                <span data-test="speed-text" class="black">
                  {{ tracker.lastSpeed | toInt }} km/h
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="device-content detail-content tab-content" id="device-content">
        <div class="container">
          <div class="row">
            <div class="col-md-5 col-sm-6 col-12">
              <div class="form-group form-row" @keypress.enter="updateDateRange">
                <div class="col-sm-3">
                  <input
                    type="text"
                    class="form-control datepicker"
                    v-model="startDateInput"
                    v-mask="'##/##/####'"
                  />
                </div>
                <div class="col-sm-2">
                  <input
                    type="text"
                    class="form-control"
                    v-model="startTimeInput"
                    v-mask="'##:##'"
                  />
                </div>
                <div class="col-sm-3">
                  <input
                    type="text"
                    class="form-control datepicker"
                    v-model="endDateInput"
                    v-mask="'##/##/####'"
                  />
                </div>
                <div class="col-sm-2">
                  <input
                    type="text"
                    class="form-control"
                    v-model="endTimeInput"
                    v-mask="'##:##'"
                  />
                </div>
              </div>
              <canvas id="speed-chart" height="200"></canvas>
              <canvas id="battery-chart" height="200"></canvas>
            </div>

            <div class="col-md-7 col-sm-6 col-12">
              <div id="map-wrapper">
                <div id="map">
                  <div id="leaflet"></div>
                </div>
                <div class="caption" v-if="tracker.latestLocation">
                  <translate>Latest location:</translate>
                  {{ latestLocation.time | date }} {{ latestLocation.time | time }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import moment from 'moment'
import Chart from 'chart.js'
import $ from 'jquery'
import NavBar from '../common/NavBar'
import DetailMap from '../../controllers/detail-map'
import baseController from '../../controllers/base'
import { format, time, toDateTime } from './date-utils'
import { findTracker } from './actions'

export default {
  extends: baseController,
  components: {
    NavBar
  },
  props: ['user'],
  data() {
    return {
      id: null,
      startDate: null,
      startDateInput: null,
      endDateInput: null,
      startTimeInput: null,
      endTimeInput: null,
      endDate: null,
      lastLocation: null,
      tracker: null
    }
  },
  computed: {
    latestLocation() {
      return this.tracker && this.tracker.latestLocation
    },
    lastSpeed() {
      return this.latestLocation && this.latestLocation.speed
    },
    lastBattery() {
      return this.latestLocation && this.latestLocation.battery
    }
  },
  async created() {
    this.id = this.$router.currentRoute.params.trackerId
    await this.loadTracker()
    this.loadMap()
    if (!this.tracker.earliestLocation || !this.tracker.latestLocation) return
    this.initDateRange()
    this.renderMap(true)
    this.renderSpeedChart()
    this.renderBatteryChart()
  },
  methods: {
    getChartDates() {
      return this.tracker.locations.map(location =>
        moment(location.time).format('D.M. H:m')
      )
    },
    getChartData(property) {
      return this.tracker.locations.map(location => location[property])
    },
    initDateRange() {
      this.initStartDates()
      this.initEndDates()
    },
    initStartDates() {
      this.startDate = moment(this.tracker.earliestLocation.time)
      this.startDateInput = format(this.startDate)
      this.startTimeInput = time(this.startDate)
    },
    initEndDates() {
      this.endDate = moment(this.tracker.latestLocation.time)
      this.endDateInput = format(this.endDate)
      this.endTimeInput = time(this.endDate)
    },
    async updateDateRange() {
      this.startDate = toDateTime(this.startDateInput, this.startTimeInput)
      this.endDate = toDateTime(this.endDateInput, this.endTimeInput)

      if (!moment(this.startDate).isValid()) {
        this.initStartDates()
        return
      }

      if (!moment(this.endDate).isValid()) {
        this.initEndDates()
        return
      }

      await this.loadTracker()
      this.renderMap(true)
      this.renderSpeedChart()
      this.renderBatteryChart()
    },
    async loadTracker() {
      this.tracker = await findTracker(this.id, this.startDate, this.endDate)
    },
    renderPropertyChart(name, property, yLabel, maxValue) {
      return new Chart($(`#${name}-chart`), {
        type: 'line',
        data: {
          labels: this.getChartDates(),
          datasets: [
            {
              label: yLabel,
              data: this.getChartData(property),
              backgroundColor: 'rgba(255, 159, 64, 0.2)',
              borderColor: 'rgba(255, 159, 64, 1)',
              borderWidth: 1
            }
          ]
        },
        options: {
          scales: {
            yAxes: [
              {
                ticks: {
                  max: maxValue,
                  min: 0,
                  stepSize: 50
                }
              }
            ]
          }
        }
      })
    },
    renderSpeedChart() {
      this.renderPropertyChart('speed', 'calculatedSpeed', 'km/h', 150)
    },
    renderBatteryChart() {
      this.renderPropertyChart('battery', 'battery', '% battery', 100)
    },
    renderMap(fitBounds, selectedLocationIdx) {
      this.map.render(this.tracker.layers(selectedLocationIdx), { fitBounds })
    },
    loadMap() {
      this.map = new DetailMap($('#leaflet', this.$el))
    }
  }
}
</script>
